import {
  AdditionalDataProperty,
  ComponentVariantsToSave,
  PageActionToSave,
} from '@app-velocity/common/types';

import { isNodeWithChildren } from '../../utils/isNodeWithChildren';
import { isNodeWithSize } from '../../utils/isNodeWithSize';
import { readReactions } from '../../utils/readReactions';
import { isNodeVisible } from '../../utils/isNodeVisible';
import { NodeWithReactions, NodeWithSize, SimpleTextNode } from '../../types';
// import { getAbsoluteCoordinates } from '../../utils/getAbsoluteCoordinates';
import { isNodeWithReactions } from '../../utils/isNodeWithReactions';
import { formatVariable } from '../../utils/variables';
// import { getNodeTextSize } from '../../utils/textNodeFontSize';
import { safeAppendNode } from '../general/safeAppendNode';
import { safeCreateNewInstance } from '../general/safeCreateNewInstance';

const getVariant = async (
  node: InstanceNode,
  component: ComponentNode,
  variantId: string,
  nodeId: string,
  tempLayer: FrameNode,
  variables: Variable[]
) => {
  const properties: AdditionalDataProperty[] = [];

  const newInstance = await safeCreateNewInstance(component, tempLayer);

  if (!newInstance) throw new Error('Unable to create instance');

  if ('componentProperties' in newInstance) {
    Object.entries(node.componentProperties)
      // Right now all properties are saved, not only those bound to variables
      // .filter(
      //   ([key, value]) =>
      //     !!value.boundVariables && !!value.boundVariables.value?.id
      // )
      // handling only variables changed by variant (eg. isSelected)
      .filter(([key, value]) => {
        return value.type === 'VARIANT';
      })
      .forEach(([key, value]) => {
        const fullVariable = variables.find(
          (variable) => variable.id === value.boundVariables?.value?.id
        );

        // reading the value that changed after component switch
        const newInstanceValue = newInstance.componentProperties[key];

        const newValue = {
          property: key,
          boundVariableId: value.boundVariables?.value?.id || null,
          value: formatVariable(
            newInstanceValue.value,
            fullVariable?.resolvedType
          ),
        };

        properties.push(newValue);
      });
  }

  newInstance.remove();

  const allNodeNestesTextNodes: TextNode[] = node.findAll(
    (subNode) => subNode.type === 'TEXT'
  ) as TextNode[];
  const allCharacters = allNodeNestesTextNodes
    .map((textNode) => textNode.characters)
    .join(' ');

  return {
    id: variantId,
    nodeId: nodeId,
    name: node.name,
    actions: [],
    properties,
    characters: allCharacters,
  };
};

const traverseNodeForActions = async (
  nodeWithReactions: BaseNode,
  originalNode: BaseNode,
  parentNode: NodeWithSize,
  mappedTextNodes: SimpleTextNode[],
  allActions: PageActionToSave[]
) => {
  if (!isNodeVisible(originalNode)) return null;

  const newActions = await readReactions(
    nodeWithReactions,
    originalNode,
    parentNode,
    mappedTextNodes
  );

  allActions.push(...newActions);

  // if (['INSTANCE', 'COMPONENT'].includes(originalNode.type)) return null;

  if (!isNodeWithChildren(originalNode)) return null;

  for (const childNode of originalNode.children) {
    await traverseNodeForActions(
      childNode,
      childNode,
      parentNode,
      mappedTextNodes,
      allActions
    );
  }
};

const getNodeActions = async (
  node: InstanceNode,
  componentVariant: ComponentNode,
  parentNode: NodeWithSize,
  tempLayer: FrameNode
) => {
  // const allTextNodes = node.findAll(
  //   (n) => n.type === 'TEXT' && !!n.visible && n.opacity > 0
  // ) as TextNode[];

  // const mappedTextNodes: SimpleTextNode[] = allTextNodes.map((textNode) => ({
  //   id: textNode.id,
  //   characters: textNode.characters,
  //   absolutePosition: getAbsoluteCoordinates(textNode),
  //   width: textNode.width,
  //   height: textNode.height,
  //   size: getNodeTextSize(textNode),
  // }));

  const mappedTextNodes: SimpleTextNode[] = [];

  // manage reaction after changing node

  const allActions: PageActionToSave[] = [];

  // new instance needs to be created to reat correct reactions from the variant
  const newInstance = await safeCreateNewInstance(componentVariant, tempLayer);

  if (!newInstance) throw new Error('Unable to create instance');

  // const nodeReactions = node.reactions;
  // newInstance.setReactionsAsync(node.reactions)

  await traverseNodeForActions(
    node,
    node,
    parentNode,
    mappedTextNodes,
    allActions
  );

  // if there are reactions in the component they need to be traversed
  if (componentVariant.reactions) {
    await traverseNodeForActions(
      newInstance,
      node,
      parentNode,
      mappedTextNodes,
      allActions
    );
  }

  newInstance.remove();

  return allActions;
};

const updateGeneratedPointsFromActions = (
  actions: PageActionToSave[],
  generatedPoints: Set<string>,
  generatedPartialPoints: Set<string>
) => {
  for (const action of actions) {
    if (action.action === 'navigate' && action.to) {
      generatedPoints.add(action.to);
    }

    if (['overlay', 'swap-overlay'].includes(action.action) && action.to) {
      generatedPartialPoints.add(action.to);
    }
  }
};

const anyMeaningfulActionInVariant = (variant: ComponentNode) => {
  return true;

  const anyImportantAction = variant.reactions.some((reaction) => {
    return (reaction.actions || []).some((action) => {
      if (
        action.type === 'NODE' &&
        ['NAVIGATE', 'SWAP', 'OVERLAY'].includes(action.navigation)
      )
        return true;
      if (action.type === 'SET_VARIABLE') return true;

      return false;
    });
  });

  return anyImportantAction;
};

// Nested elements need to be checked as well, a child element can change parent variant
const getAllVariantChangeToDestinations = (
  defaultVariant: InstanceNode | ComponentNode,
  allVariants: ComponentNode[],
  mainComponent: ComponentNode,
  dontSkipVariants = false
): string[] => {
  const variantIdsToCheck: string[] = [];
  const destinationIds: string[] = [];
  const actions: {
    trigger: Trigger['type'] | null;
    action: Action['type'];
    navigation?: Navigation;
  }[] = [];
  destinationIds.push(defaultVariant.id);

  const allNodesWithReactions: NodeWithReactions[] = defaultVariant.findAll(
    (n) => isNodeWithReactions(n) && n.reactions.length > 0
  ) as NodeWithReactions[];

  for (const nodeWithReactions of [...allNodesWithReactions, defaultVariant]) {
    for (const reaction of nodeWithReactions.reactions) {
      for (const action of reaction.actions || []) {
        if (
          action.type === 'NODE' &&
          action.navigation === 'CHANGE_TO' &&
          action.destinationId
        ) {
          actions.push({
            trigger: reaction.trigger?.type || null,
            action: action.type,
            navigation: action.navigation,
          });
          if (destinationIds.includes(action.destinationId)) continue;
          destinationIds.push(action.destinationId);
          variantIdsToCheck.push(action.destinationId);
        }

        if (action.type !== 'NODE') {
          actions.push({
            trigger: reaction.trigger?.type || null,
            action: action.type,
          });
        }
      }
    }
  }

  while (variantIdsToCheck.length > 0) {
    const variantIdToCheck = variantIdsToCheck.shift();
    const variantToCheck = allVariants.find((v) => v.id === variantIdToCheck);

    if (!variantToCheck) continue;

    if (variantToCheck && anyMeaningfulActionInVariant(variantToCheck)) {
      const allNodesWithReactions: NodeWithReactions[] = variantToCheck.findAll(
        (n) => isNodeWithReactions(n) && n.reactions.length > 0
      ) as NodeWithReactions[];

      for (const node of [...allNodesWithReactions, variantToCheck]) {
        for (const reaction of node.reactions) {
          for (const action of reaction.actions || []) {
            if (
              action.type === 'NODE' &&
              action.navigation === 'CHANGE_TO' &&
              action.destinationId
            ) {
              actions.push({
                trigger: reaction.trigger?.type || null,
                action: action.type,
                navigation: action.navigation,
              });
              if (destinationIds.includes(action.destinationId)) continue;
              destinationIds.push(action.destinationId);
              variantIdsToCheck.push(action.destinationId);
            }

            if (action.type !== 'NODE') {
              actions.push({
                trigger: reaction.trigger?.type || null,
                action: action.type,
              });
            }
          }
        }
      }
    }
  }

  const anyMeaningfulAction = dontSkipVariants
    ? true
    : actions.some((action) => {
        if (action.trigger === 'ON_HOVER' && action.navigation !== 'CHANGE_TO')
          return true;
        if (action.trigger !== 'ON_HOVER') return true;

        return false;
      });

  if (!anyMeaningfulAction) return [];

  // no point in generating separate variant when only the default one is used
  if (destinationIds.length === 1 && destinationIds.includes(defaultVariant.id))
    return [];

  return destinationIds;
};

const isPropertiesMatch = (
  staticDefaultProperties: { [key: string]: string | boolean },
  variantProperties: { [key: string]: string | boolean }
) => {
  const defaultKeys = Object.keys(staticDefaultProperties);
  const variantKeys = Object.keys(variantProperties);

  if (defaultKeys.length !== variantKeys.length) return false;

  for (const key of defaultKeys) {
    if (typeof variantProperties[key] === 'undefined') return false;
    if (staticDefaultProperties[key] !== variantProperties[key]) return false;
  }

  return true;
};

const mapComponentVariants = (
  mainComponent: ComponentNode,
  dontSkipVariants = false
) => {
  // const start = new Date().getTime();
  // console.log('CREATING MAP START');
  if (!mainComponent || !mainComponent.parent) return;
  const variantSet = mainComponent.parent.children as ComponentNode[];

  const destinationsMap = new Map<string, string[]>();
  const componentsMap = new Map<string, ComponentNode>();

  for (const variant of variantSet) {
    const destinationIds = getAllVariantChangeToDestinations(
      variant,
      variantSet,
      mainComponent,
      dontSkipVariants
    );

    destinationsMap.set(variant.id, destinationIds);
    componentsMap.set(variant.id, variant);
  }

  // const end = new Date().getTime();

  // console.log('CREATING MAP FINISH', ((end - start) / 1000).toFixed(4));
  return { destinationsMap, componentsMap };
};

const traverseNode = async (
  parentNode: BaseNode,
  node: BaseNode,
  pageNode: BaseNode,
  variants: ComponentVariantsToSave[],
  generatedPoints: Set<string>,
  generatedPartialPoints: Set<string>,
  generatedVariantPoints: Set<string>,
  warnings: { nodeName: string; nodeId: string }[],
  handledVariantIDs: string[],
  depth: number,
  clonedIdsMap: { [clonedId: string]: string },
  originalIdsMap: { [clonedId: string]: string },
  tempLayer: FrameNode,
  byId: Map<string, SceneNode>,
  variantsMap: Map<
    string,
    {
      destinationIds: Map<string, string[]>;
      componentsMap: Map<string, ComponentNode>;
    }
  >,
  variables: Variable[],
  dontSkipVariants = false
) => {
  if (!isNodeVisible(node)) return null;
  if (!isNodeWithSize(parentNode)) return;

  if (!isNodeWithReactions(node)) return;

  if (node.type === 'INSTANCE') {
    const boundVariables = node.boundVariables;

    const mainComponent = await node.getMainComponentAsync();
    // const start = new Date().getTime();
    // console.log(
    //   'HANDLING INSTANCE',
    //   node.id,
    //   node.name,
    //   mainComponent?.id,
    //   mainComponent?.name,
    //   mainComponent?.parent?.id,
    //   mainComponent?.parent?.name,
    //   node,
    //   mainComponent,
    //   mainComponent?.parent
    // );

    if (!mainComponent) return;
    const variantSet = mainComponent.parent;

    if (!variantSet) return;

    const defaultVariantId = mainComponent.id;

    const newComponentVariant: ComponentVariantsToSave = {
      parentId: parentNode.id,
      pageId: pageNode.id,
      instanceId: node.id,
      variants: [],
    };

    let destinationIds: string[] = [];
    let componentMap: Map<string, ComponentNode> | null;

    if (variantsMap.has(mainComponent.parent.id)) {
      // console.log(
      //   'REUSING MAP FOR',
      //   mainComponent.parent.name,
      //   mainComponent.parent.id
      // );
      const destinationsMap = variantsMap.get(mainComponent.parent.id);
      destinationIds =
        destinationsMap?.destinationIds.get(defaultVariantId) || [];
      componentMap = destinationsMap?.componentsMap || null;
    } else {
      // console.log(
      //   'CREATING MAP MAP FOR',
      //   mainComponent.parent.name,
      //   mainComponent.parent.id
      // );
      const res = mapComponentVariants(mainComponent, dontSkipVariants);
      if (!res) return;
      const { destinationsMap, componentsMap } = res;
      destinationIds = destinationsMap?.get(defaultVariantId) || [];
      componentMap = componentsMap;
      if (destinationsMap)
        variantsMap.set(mainComponent.parent.id, {
          destinationIds: destinationsMap,
          componentsMap,
        });
    }

    // console.log(
    //   'HANDLING INSTANCE',
    //   'MAPPED DESTINATIONS',
    //   node.id,
    //   node.name,
    //   defaultVariantId,
    //   destinationIds,
    //   variantsMap
    // );

    // const destinationIds = getAllVariantChangeToDestinations(
    //   node,
    //   allVariants,
    //   mainComponent
    // );

    if (!componentMap) return;

    // if there is no bound variables to properties that will switch the variant
    // nor there is no change to actions
    if (!boundVariables?.componentProperties && destinationIds.length === 0)
      return;

    const boundVariablesToProperties = Object.keys(
      boundVariables?.componentProperties || {}
    );

    const propertiesBoundToFileVariables = boundVariablesToProperties
      .map((property) => {
        if (!boundVariables?.componentProperties) return null;
        const fullProperty =
          boundVariables?.componentProperties[property] || null;
        if (!fullProperty) return null;
        const boundVariable = fullProperty.id;
        const fullVariable = variables.find((v) => v.id === boundVariable);

        return fullVariable ? property : null;
      })
      .filter((v) => !!v) as string[];

    if (
      propertiesBoundToFileVariables.length === 0 &&
      destinationIds.length === 0
    )
      return;

    // getting the variant properties that are not controlled by any variable
    const defaultVariantStaticProperties: { [key: string]: string | boolean } =
      {};

    for (const variantProperty of Object.keys(node.componentProperties || {})) {
      if (
        !!boundVariables?.componentProperties &&
        typeof boundVariables.componentProperties[variantProperty] !==
          'undefined'
      )
        continue;
      defaultVariantStaticProperties[variantProperty] =
        node.componentProperties![variantProperty].value;
    }

    /******* UPDATED - going only over destination ids*/
    // const dStart = new Date().getTime();
    // console.log('STARTING HANDLING DESTINATOINS', node.name, node.id);
    if (!dontSkipVariants) {
      for (const destinationId of [...destinationIds]) {
        // console.log('DESTINATION_HANDLING 1', node.name, node.id, destinationId);
        const componentVariant = componentMap.get(destinationId);
        // console.log('DESTINATION_HANDLING 2', componentVariant);
        if (!componentVariant) continue;

        if (depth === 1 /*|| handledVariantIDs.includes(componentVariant.id)*/)
          continue;

        if (componentVariant.type !== 'COMPONENT') {
          continue;
        }

        // console.log('DESTINATION_HANDLING 3');
        const newInstance = await safeCreateNewInstance(
          componentVariant,
          tempLayer
        );
        // console.log('DESTINATION_HANDLING 4');
        if (!newInstance) throw new Error('Unable to create instance');

        const variantProperties = Object.entries(
          newInstance.componentProperties || {}
        )
          .filter(([_, prop]) => prop.type === 'VARIANT')
          .map(([key, _]) => key);

        const variantStaticProperties: { [key: string]: string | boolean } = {};

        for (const variantProperty of Object.keys(
          newInstance.componentProperties || {}
        )) {
          if (
            !!boundVariables?.componentProperties &&
            typeof boundVariables.componentProperties[variantProperty] !==
              'undefined'
          )
            continue;
          variantStaticProperties[variantProperty] =
            newInstance.componentProperties![variantProperty].value;
        }

        // console.log('DESTINATION_HANDLING 5');
        // if new variant has the same static properties as the default one
        const propertiesMatch = isPropertiesMatch(
          defaultVariantStaticProperties,
          variantStaticProperties
        );

        // console.log('DESTINATION_HANDLING 6');
        // @TODO needs to be adjusted for music player
        // variant needs to be generated if the is change action or a variables is controlling its property
        // and that variable exist in file variables
        if (
          !destinationIds.includes(componentVariant.id) &&
          (!propertiesMatch ||
            (propertiesMatch &&
              !propertiesBoundToFileVariables.some((boundProperty) =>
                variantProperties.includes(boundProperty)
              )))
        ) {
          newInstance.remove();
          continue;
        }

        try {
          node.swapComponent(componentVariant);
        } catch {
          warnings.push({
            nodeName: node.name,
            nodeId: node.id,
          });
          newInstance.remove();
          continue;
        }

        // console.log('DESTINATION_HANDLING 7');
        const isDefault = componentVariant.id === defaultVariantId;

        if (!tempLayer) return;

        const nodeClone = node.clone();
        // console.log('DESTINATION_HANDLING 8');
        const propertiesToSet: {
          [property: string]: string | boolean | VariableAlias;
        } = {};

        for (const key of Object.keys(nodeClone.componentProperties)) {
          propertiesToSet[key] = nodeClone.componentProperties[key].value;
        }

        for (const key of Object.keys(newInstance.componentProperties)) {
          const property = newInstance.componentProperties[key];

          if (property.type === 'VARIANT') {
            propertiesToSet[key] = property.value;
          }
        }

        // console.log('DESTINATION_HANDLING 9');
        nodeClone.setProperties(propertiesToSet);

        clonedIdsMap[nodeClone.id] = node.id;
        originalIdsMap[node.id] = nodeClone.id;
        // console.log('DESTINATION_HANDLING 10');
        generatedVariantPoints.add(nodeClone.id);
        nodeClone.name = `${parentNode.id}_${node.id}_${componentVariant.id}${
          isDefault ? '_default' : ''
        }`;
        // nodeClone.visible = false;
        // nodeClone.opacity = 0;

        await safeAppendNode(nodeClone, tempLayer);
        // console.log('DESTINATION_HANDLING 11');
        byId.set(nodeClone.id, nodeClone);
        // console.log('DESTINATION_HANDLING 12');
        const generatedVariant = await getVariant(
          node,
          componentVariant,
          componentVariant.id,
          nodeClone.id,
          tempLayer,
          variables
        );
        // console.log('DESTINATION_HANDLING 13');
        const visibleWhen: {
          [variableId: string]: string | number | boolean;
        }[] = [];

        for (const property of boundVariablesToProperties) {
          if (
            !boundVariables?.componentProperties ||
            !boundVariables.componentProperties[property]
          )
            continue;

          const binding = boundVariables.componentProperties[property];
          const fullVariable = variables.find(
            (variable) => variable.id === binding.id
          );

          const variantProperty = newInstance.componentProperties
            ? newInstance.componentProperties[property].value
            : null;

          if (
            binding.type === 'VARIABLE_ALIAS' &&
            variantProperty &&
            fullVariable
          ) {
            visibleWhen.push({
              [binding.id]: formatVariable(
                variantProperty,
                fullVariable.resolvedType
              ),
            });
          }
        }

        // console.log('DESTINATION_HANDLING 14');
        const variantActions = await getNodeActions(
          node,
          componentVariant,
          parentNode,
          tempLayer
        );
        // console.log('DESTINATION_HANDLING 15');
        newComponentVariant.variants.push({
          ...generatedVariant,
          parentId: parentNode.id,
          default: isDefault,
          visibleWhen,
          actions: variantActions,
        });

        newInstance.remove();
      }
    }
    // const dEnd = new Date().getTime();
    // console.log(
    //   'FINISHED HANDLING DESTINATOINS',
    //   ((dEnd - dStart) / 1000).toFixed(2)
    // );
    /******* */

    /* PREVIOUS */
    if (dontSkipVariants) {
      const allVariants = variantSet.children as ComponentNode[];

      for (const componentVariant of allVariants) {
        if (depth === 1 /*|| handledVariantIDs.includes(componentVariant.id)*/)
          continue;

        if (componentVariant.type !== 'COMPONENT') {
          continue;
        }

        const newInstance = await safeCreateNewInstance(
          componentVariant,
          tempLayer
        );

        if (!newInstance) throw new Error('Unable to create instance');

        const variantProperties = Object.entries(
          newInstance.componentProperties || {}
        )
          .filter(([_, prop]) => prop.type === 'VARIANT')
          .map(([key, _]) => key);

        const variantStaticProperties: { [key: string]: string | boolean } = {};

        for (const variantProperty of Object.keys(
          newInstance.componentProperties || {}
        )) {
          if (
            !!boundVariables?.componentProperties &&
            typeof boundVariables.componentProperties[variantProperty] !==
              'undefined'
          )
            continue;
          variantStaticProperties[variantProperty] =
            newInstance.componentProperties![variantProperty].value;
        }

        // if new variant has the same static properties as the default one
        const propertiesMatch = isPropertiesMatch(
          defaultVariantStaticProperties,
          variantStaticProperties
        );

        // @TODO needs to be adjusted for music player
        // variant needs to be generated if the is change action or a variables is controlling its property
        // and that variable exist in file variables
        if (
          !destinationIds.includes(componentVariant.id) &&
          (!propertiesMatch ||
            (propertiesMatch &&
              !propertiesBoundToFileVariables.some((boundProperty) =>
                variantProperties.includes(boundProperty)
              )))
        ) {
          newInstance.remove();
          continue;
        }

        try {
          node.swapComponent(componentVariant);
        } catch {
          warnings.push({
            nodeName: node.name,
            nodeId: node.id,
          });
          newInstance.remove();
          continue;
        }

        const isDefault = componentVariant.id === defaultVariantId;

        if (!tempLayer) return;

        const nodeClone = node.clone();

        const propertiesToSet: {
          [property: string]: string | boolean | VariableAlias;
        } = {};

        for (const key of Object.keys(nodeClone.componentProperties)) {
          propertiesToSet[key] = nodeClone.componentProperties[key].value;
        }

        for (const key of Object.keys(newInstance.componentProperties)) {
          const property = newInstance.componentProperties[key];

          if (property.type === 'VARIANT') {
            propertiesToSet[key] = property.value;
          }
        }

        nodeClone.setProperties(propertiesToSet);

        clonedIdsMap[nodeClone.id] = node.id;
        originalIdsMap[node.id] = nodeClone.id;

        generatedVariantPoints.add(nodeClone.id);
        nodeClone.name = `${parentNode.id}_${node.id}_${componentVariant.id}${
          isDefault ? '_default' : ''
        }`;
        // nodeClone.visible = false;
        // nodeClone.opacity = 0;

        await safeAppendNode(nodeClone, tempLayer);
        byId.set(nodeClone.id, nodeClone);

        const generatedVariant = await getVariant(
          node,
          componentVariant,
          componentVariant.id,
          nodeClone.id,
          tempLayer,
          variables
        );

        const visibleWhen: {
          [variableId: string]: string | number | boolean;
        }[] = [];

        for (const property of boundVariablesToProperties) {
          if (
            !boundVariables?.componentProperties ||
            !boundVariables.componentProperties[property]
          )
            continue;

          const binding = boundVariables.componentProperties[property];
          const fullVariable = variables.find(
            (variable) => variable.id === binding.id
          );

          const variantProperty = newInstance.componentProperties
            ? newInstance.componentProperties[property].value
            : null;

          if (
            binding.type === 'VARIABLE_ALIAS' &&
            variantProperty &&
            fullVariable
          ) {
            visibleWhen.push({
              [binding.id]: formatVariable(
                variantProperty,
                fullVariable.resolvedType
              ),
            });
          }
        }

        const variantActions = await getNodeActions(
          node,
          componentVariant,
          parentNode,
          tempLayer
        );

        newComponentVariant.variants.push({
          ...generatedVariant,
          parentId: parentNode.id,
          default: isDefault,
          visibleWhen,
          actions: variantActions,
        });

        newInstance.remove();
      }
    }
    /* */
    // const end = new Date().getTime();
    // console.log(
    //   'HANDLING INSTANCE ALL VARIANTS',
    //   node.id,
    //   node.name,
    //   newComponentVariant,
    //   ((end - start) / 1000).toFixed(4)
    // );

    if (newComponentVariant.variants.length > 1) {
      node.opacity = 0;
      variants.push(newComponentVariant);

      for (const variant of newComponentVariant.variants) {
        updateGeneratedPointsFromActions(
          variant.actions,
          generatedPoints,
          generatedPartialPoints
        );
      }

      node.swapComponent(mainComponent);
    }
  }

  if (!isNodeWithChildren(node)) return null;
};

export const getVariantsPointsData = async (
  node: BaseNode,
  pageNode: BaseNode,
  tempLayer: FrameNode,
  handledVariantIDs: string[],
  warnings: { nodeName: string; nodeId: string }[],
  depth: number,
  byId: Map<string, SceneNode>,
  variantsMap: Map<
    string,
    {
      destinationIds: Map<string, string[]>;
      componentsMap: Map<string, ComponentNode>;
    }
  >,
  variables: Variable[],
  dontSkipVariants = false
) => {
  const variants: ComponentVariantsToSave[] = [];
  const generatedPoints = new Set<string>();
  const generatedPartialPoints = new Set<string>();
  const generatedVariantPoints = new Set<string>();
  const clonedIdsMap: { [clonedId: string]: string } = {};
  const originalIdsMap: { [originalId: string]: string } = {};

  try {
    await traverseNode(
      node,
      node,
      pageNode,
      variants,
      generatedPoints,
      generatedPartialPoints,
      generatedVariantPoints,
      warnings,
      handledVariantIDs,
      depth,
      clonedIdsMap,
      originalIdsMap,
      tempLayer,
      byId,
      variantsMap,
      variables,
      dontSkipVariants
    );
  } catch {
    warnings.push({
      nodeName: node.name,
      nodeId: node.id,
    });
  }

  return {
    variants: [...variants],
    generatedPoints: [...generatedPoints],
    generatedPartialPoints: [...generatedPartialPoints],
    generatedVariantPoints: [...generatedVariantPoints],
    originalIdsMap,
    clonedIdsMap,
  };
};
